import { z } from 'zod'
import { isValidAadhaar, normaliseAadhaar } from '../aadhaar.ts'
import { nonEmpty, optionalText, phone, uuid } from './common.ts'

// Field names follow the printed form, so a data-entry operator reading the
// paper form top to bottom meets them in the same order and wording.
export const SEXES = ['male', 'female', 'other'] as const
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const

export const patientFields = z.object({
  fullName: nonEmpty(160),                    // (1)
  fatherName: optionalText(160),              // (2)
  age: z.number().int().min(0).max(130).optional().nullable(),  // (3)
  sex: z.enum(SEXES).optional().nullable(),   // (4)
  maritalStatus: optionalText(40),            // (5)
  education: optionalText(80),                // (6)
  caste: optionalText(80),                    // (7)
  occupation: optionalText(80),               // (8)
  phone: phone.optional().nullable(),         // (9)
  addressLine1: optionalText(240),            // (10)
  tehsil: optionalText(80),                   // (11)
  district: optionalText(80),                 // (12)
  state: optionalText(80),                    // (13)
  pincode: z.string().trim().regex(/^[0-9]{6}$/).optional().nullable(),
  bloodGroup: z.enum(BLOOD_GROUPS).optional().nullable(),        // (17)

  // Stored as twelve digits; spaces the operator typed are removed. The
  // check digit is verified offline -- see aadhaar.ts for why there is no
  // API call here.
  aadhaar: z.string()
    .trim()
    .transform(v => normaliseAadhaar(v))
    .refine(v => v === '' || isValidAadhaar(v), {
      message: 'That Aadhaar number is not valid — check the digits',
    })
    .transform(v => (v === '' ? null : v))
    .optional()
    .nullable(),
})

export const createPatient = patientFields.extend({ id: uuid })
export type CreatePatient = z.infer<typeof createPatient>

export const updatePatient = patientFields.partial().extend({
  version: z.number().int().positive(),
})
export type UpdatePatient = z.infer<typeof updatePatient>
